import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, Truck, Sprout, Utensils, ShoppingBasket, Star } from "lucide-react";

const CATEGORIES = [
  { key: "fruits", label: "Fresh Fruits", img: "https://images.unsplash.com/photo-1619566636858-adf3ef46400b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMGZydWl0cyUyMGJhc2tldHxlbnwwfHx8fDE3ODMwMjM0OTN8MA&ixlib=rb-4.1.0&q=85", desc: "Sun-ripened, hand-picked seasonal fruits." },
  { key: "vegetables", label: "Vegetables", img: "https://images.unsplash.com/photo-1597362925123-77861d3fbac7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxmcmVzaCUyMG9yZ2FuaWMlMjB2ZWdldGFibGVzfGVufDB8fHx8MTc4MzAyMzQ3OXww&ixlib=rb-4.1.0&q=85", desc: "Farm-fresh greens, tubers and roots." },
  { key: "grocery", label: "Grocery", img: "https://images.unsplash.com/photo-1542838132-92c53300491e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxvcmdhbmljJTIwZnJlc2glMjBncm9jZXJ5JTIwbWFya2V0fGVufDB8fHx8MTc4MzAyMzQ3OXww&ixlib=rb-4.1.0&q=85", desc: "Everyday essentials, ethically sourced." },
  { key: "restaurant", label: "Restaurant Meals", img: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwbWVhbCUyMGZsYXRsYXl8ZW58MHx8fHwxNzgzMDIzNDc5fDA&ixlib=rb-4.1.0&q=85", desc: "Chef-cooked meals, ready in minutes." },
];

const Home = () => {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } })
      .then((r) => setFeatured(r.data))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 pt-10 md:pt-16 pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="tick-label mb-4">Farm → Kitchen · Delivered Daily</div>
            <h1 className="font-heading text-5xl sm:text-6xl leading-[0.95] tracking-tight text-[#2C3E2D] font-medium">
              Fresh grocery, <br />
              <span className="italic text-[#E07A5F]">real</span> ingredients,
              <br />
              honest meals.
            </h1>
            <p className="mt-6 text-base leading-relaxed text-[#6B7B6C] max-w-xl">
              Gramo brings you everything you need for the week — sourced from local farms and neighborhood kitchens.
              One basket. Free delivery over ₹499.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/shop" className="btn-primary rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2" data-testid="hero-shop-cta">
                Shop the market <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/shop?category=restaurant" className="btn-outline-dark rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2" data-testid="hero-meals-cta">
                Today&apos;s Meals <Utensils className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-[#6B7B6C]">
              <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-[#81B29A]" /> Same-day delivery</div>
              <div className="flex items-center gap-2"><Sprout className="h-4 w-4 text-[#81B29A]" /> 100% farm-fresh</div>
              <div className="flex items-center gap-2 hidden sm:flex"><Star className="h-4 w-4 text-[#F2CC8F] fill-[#F2CC8F]" /> 4.9 avg rating</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="hero-frame relative aspect-[4/5] max-h-[72vh]">
              <img
                src="https://images.unsplash.com/photo-1628102491629-778571d893a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxvcmdhbmljJTIwZnJlc2glMjBncm9jZXJ5JTIwbWFya2V0fGVufDB8fHx8MTc4MzAyMzQ3OXww&ixlib=rb-4.1.0&q=85"
                alt="Fresh market"
                className="h-full w-full object-cover img-cap"
              />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-2xl bg-white/85 backdrop-blur-md p-4 border border-white/60">
                <div>
                  <div className="tick-label">Weekly Basket</div>
                  <div className="font-heading text-lg text-[#2C3E2D]">Starting at ₹599</div>
                </div>
                <Link to="/shop" className="rounded-full bg-[#2C3E2D] text-[#F9F8F6] px-4 py-2 text-xs font-medium hover:bg-[#E07A5F] transition-colors" data-testid="hero-basket-cta">Explore</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES — Bento */}
      <section className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="tick-label mb-2">Shop by category</div>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#2C3E2D] tracking-tight font-medium">One store. Everything fresh.</h2>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-1 text-sm text-[#2C3E2D] hover:text-[#E07A5F]" data-testid="cat-view-all">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {CATEGORIES.map((c) => (
            <Link
              key={c.key}
              to={`/shop?category=${c.key}`}
              data-testid={`category-tile-${c.key}`}
              className="group relative rounded-3xl overflow-hidden border border-[#E5E5E0] bg-white card-lift"
            >
              <div className="aspect-[3/4] overflow-hidden">
                <img src={c.img} alt={c.label} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C3E2D]/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                <div className="font-heading text-lg md:text-xl text-white font-medium">{c.label}</div>
                <div className="text-xs text-white/85 mt-0.5 hidden md:block">{c.desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="tick-label mb-2">This week&apos;s picks</div>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#2C3E2D] tracking-tight font-medium">Featured today</h2>
          </div>
          <Link to="/shop" className="text-sm text-[#2C3E2D] hover:text-[#E07A5F] inline-flex items-center gap-1" data-testid="featured-view-all">
            All products <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {featured.map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </section>

      {/* PROMISE */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <PromiseTile Icon={Sprout} title="From the source" desc="Direct partnerships with 200+ farms and neighborhood kitchens." />
          <PromiseTile Icon={Truck} title="Under 60 mins" desc="Express delivery on essentials in most metro pincodes." />
          <PromiseTile Icon={ShoppingBasket} title="Zero waste" desc="Reusable crates, compostable packaging, honest pricing." />
        </div>
      </section>
    </div>
  );
};

const PromiseTile = ({ Icon, title, desc }) => (
  <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6">
    <div className="h-10 w-10 rounded-full bg-[#F0EFEA] inline-flex items-center justify-center mb-4">
      <Icon className="h-5 w-5 text-[#81B29A]" strokeWidth={1.5} />
    </div>
    <div className="font-heading text-lg text-[#2C3E2D] font-medium mb-1">{title}</div>
    <div className="text-sm text-[#6B7B6C] leading-relaxed">{desc}</div>
  </div>
);

export default Home;
